import { createContext, useContext, useEffect, useState } from 'react'
import { User, Session, SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '../types/supabase'
import { API_ENDPOINTS, getAuthHeaders, shouldIncludeCredentials } from '../lib/config/api-endpoints'
import { getSupabaseClient } from '../lib/supabase'
import { 
  isLocalDevMode, 
  getCurrentDevUser, 
  createMockSession, 
  getDevUserSwitcherConfig,
  logDevAuthStatus 
} from '../lib/dev-auth-bypass'

type UserRole = Database['public']['Enums']['user_role']

interface UserProfile {
  id: string
  role: UserRole
  display_name: string | null
  first_name: string | null
  last_name: string | null
  phone: string | null
  metadata: any
}

interface AuthUser extends User {
  profile?: UserProfile | null
  // Legacy properties
  clientId?: string
  name?: string
  intake_data?: any
  first_name?: string
}

interface AuthState {
  user: AuthUser | null
  session: Session | null
  loading: boolean
  profile: UserProfile | null
}

interface AuthContextType extends AuthState {
  supabase: SupabaseClient<Database>
  signInWithMagicLink: (email: string, callbackURL?: string) => Promise<{ success: boolean }>
  signInWithPassword: (email: string, password: string) => Promise<{ success: boolean; user?: AuthUser }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  isAuthenticated: boolean
  isAdmin: boolean
  isClient: boolean
  isPractitioner: boolean
  // Legacy properties for backward compatibility
  isLoading: boolean
  error: Error | null
  client: AuthUser | null
  signIn: (email: string, password: string) => Promise<{ success: boolean; user?: AuthUser }>
  logout: () => Promise<void>
  // Dev mode properties
  isDevMode: boolean
  devUserSwitcher: any
  switchDevUser: (userKey: string) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function SupabaseAuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    profile: null
  })

  // Check if we're in dev mode
  const isDevMode = isLocalDevMode()
  
  // Use singleton Supabase client to prevent multiple instances
  const supabase = getSupabaseClient()

  // Fetch user profile
  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    // In dev mode, return mock profile based on current dev user
    if (isDevMode) {
      const devUser = getCurrentDevUser()
      if (devUser) {
        return {
          id: devUser.id,
          role: devUser.role as UserRole,
          display_name: devUser.display_name || null,
          first_name: devUser.first_name || null,
          last_name: devUser.last_name || null,
          phone: devUser.phone || null,
          metadata: devUser.metadata || {}
        }
      }
    }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error fetching profile:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Profile fetch error:', error)
      return null
    }
  }

  // Update auth state with memoization to prevent unnecessary re-renders
  const updateAuthState = async (session: Session | null) => {
    if (session?.user) {
      const profile = await fetchProfile(session.user.id)
      setState(prevState => {
        // Only update if something actually changed
        if (prevState.session?.user?.id === session.user.id && 
            prevState.profile?.id === profile?.id &&
            !prevState.loading) {
          return prevState
        }
        
        return {
          user: {
            ...session.user,
            profile,
            // Add legacy properties
            clientId: session.user.id,
            name: profile?.display_name || profile?.first_name || session.user.email || '',
            first_name: profile?.first_name || '',
            intake_data: profile?.metadata?.intake_data || null
          },
          session,
          profile,
          loading: false
        }
      })
    } else {
      setState(prevState => {
        // Only clear if we actually had a user before
        if (!prevState.user && !prevState.loading) {
          return prevState
        }
        
        return {
          user: null,
          session: null,
          profile: null,
          loading: false
        }
      })
    }
  }

  // Initialize auth state
  useEffect(() => {
    let mounted = true
    let authChangeTimeout: NodeJS.Timeout | null = null

    // Handle dev mode bypass
    if (isDevMode) {
      const devUser = getCurrentDevUser()
      if (devUser) {
        console.log('🔧 DEV MODE: Using dev user:', devUser.display_name, devUser.role)
        logDevAuthStatus()
        
        // Create mock session for dev user
        const mockSession = createMockSession(devUser) as any
        updateAuthState(mockSession)
        return
      }
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        console.log('Auth state changed: INITIAL_SESSION', session?.user?.email || 'undefined')
        updateAuthState(session)
      }
    })

    // Listen for auth changes with debouncing to prevent loops
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return
        
        console.log('Auth state changed:', event, session?.user?.email || 'undefined')
        
        // Clear any pending auth updates
        if (authChangeTimeout) {
          clearTimeout(authChangeTimeout)
        }
        
        // Debounce auth state updates to prevent rapid changes
        authChangeTimeout = setTimeout(async () => {
          if (mounted) {
            await updateAuthState(session)
          }
        }, 100)
      }
    )

    return () => {
      mounted = false
      if (authChangeTimeout) {
        clearTimeout(authChangeTimeout)
      }
      subscription.unsubscribe()
    }
  }, [isDevMode]) // Add isDevMode to dependencies

  // Auth methods
  const signInWithMagicLink = async (email: string, callbackURL?: string) => {
    try {
      const response = await fetch('/api/auth/magic-link/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          callbackURL: callbackURL || '/portal/dashboard'
        }),
      })

      const result = await response.json()
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send magic link')
      }

      // Handle local development auto-redirect
      const isLocalDev = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      
      if (isLocalDev && result.magic_link) {
        console.log('🔧 LOCAL DEV: Auto-redirecting to magic link', result.magic_link)
        // Small delay to show the sending state, then redirect
        setTimeout(() => {
          window.location.href = result.magic_link
        }, 500)
      }

      return { success: true }
    } catch (error: any) {
      console.error('Magic link error:', error)
      throw new Error(error.message || 'Failed to send magic link')
    }
  }

  const signInWithPassword = async (email: string, password: string) => {
    try {
      // Use Edge Function for signin
      const response = await fetch(API_ENDPOINTS.auth.signin, {
        method: 'POST',
        headers: getAuthHeaders(API_ENDPOINTS.auth.signin),
        body: JSON.stringify({ email, password }),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.auth.signin) ? 'include' : 'same-origin'
      })

      const result = await response.json()
      
      if (!response.ok) {
        throw new Error(result.error || 'Authentication failed')
      }

      // If we got a session back, update the auth state
      if (result.session) {
        const { data } = await supabase.auth.getSession()
        if (data.session) {
          await updateAuthState(data.session)
        }
      }
      
      return { 
        success: true,
        user: result.user
      }
    } catch (error: any) {
      console.error('Password signin error:', error)
      throw new Error(error.message || 'Authentication failed')
    }
  }

  const signOut = async () => {
    try {
      // In dev mode, just clear state and localStorage
      if (isDevMode) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('dev_auth_user')
        }
        setState({
          user: null,
          session: null,
          profile: null,
          loading: false
        })
        return
      }

      // Call Edge Function signout to clear cookies
      await fetch(API_ENDPOINTS.auth.signout, {
        method: 'POST',
        headers: getAuthHeaders(API_ENDPOINTS.auth.signout),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.auth.signout) ? 'include' : 'same-origin'
      })

      // Also sign out from Supabase client
      await supabase.auth.signOut()
      
      setState({
        user: null,
        session: null,
        profile: null,
        loading: false
      })
    } catch (error) {
      console.error('Signout error:', error)
      // Force clear state even if signout fails
      setState({
        user: null,
        session: null,
        profile: null,
        loading: false
      })
    }
  }

  const refreshProfile = async () => {
    if (state.user?.id) {
      const profile = await fetchProfile(state.user.id)
      setState(prev => ({
        ...prev,
        profile,
        user: prev.user ? { ...prev.user, profile } : null
      }))
    }
  }

  // Dev mode helper functions
  const switchDevUser = (userKey: string) => {
    if (isDevMode && typeof window !== 'undefined') {
      localStorage.setItem('dev_auth_user', userKey)
      window.location.reload()
    }
  }

  // Computed properties
  const isAuthenticated = !!state.user && !!state.session
  const isAdmin = state.profile?.role === 'admin'
  const isClient = state.profile?.role === 'client'
  const isPractitioner = state.profile?.role === 'practitioner'

  const value: AuthContextType = {
    ...state,
    supabase,
    signInWithMagicLink,
    signInWithPassword,
    signOut,
    refreshProfile,
    isAuthenticated,
    isAdmin,
    isClient,
    isPractitioner,
    // Legacy properties for backward compatibility
    isLoading: state.loading,
    error: null,
    client: state.user,
    signIn: signInWithPassword,
    logout: signOut,
    // Dev mode properties
    isDevMode,
    devUserSwitcher: getDevUserSwitcherConfig(),
    switchDevUser
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useSupabaseAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider')
  }
  return context
}

// Backwards compatibility - keep the same interface as the old useAuth
export const useAuth = useSupabaseAuth
export const AuthProvider = SupabaseAuthProvider